/**
 * REST API 向けに安全な query string を生成する。
 */

export type QueryParameterPrimitive = string | number | boolean | bigint

export type QueryParameterValue =
  | QueryParameterPrimitive
  | ReadonlyArray<QueryParameterPrimitive | null | undefined>
  | null
  | undefined

export type QueryParams = Record<string, QueryParameterValue>

/**
 * query parameter として許可する primitive 値かを判定する。
 * @param value - 判定対象の値
 * @returns 許可された primitive 値の場合は true
 */
const isQueryParameterPrimitive = (
  value: unknown,
): value is QueryParameterPrimitive =>
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean' ||
  typeof value === 'bigint'

/**
 * 1 件分の query entry を URL エンコードする。
 * @param key - query key
 * @param value - query value
 * @returns エンコード済みの query entry
 */
const encodeQueryEntry = (params: {
  key: string
  value: QueryParameterPrimitive
}): string => {
  const { key, value } = params
  return `${encodeURIComponent(key)}=${encodeURIComponent(value.toString())}`
}

/**
 * query object をソート済みの query entries に展開する。
 * @param params - query parameter object
 * @returns query entries
 */
const buildQueryEntries = (params: Record<string, unknown>): string[] => {
  const entries: string[] = []

  for (const key of Object.keys(params).sort()) {
    const value = params[key]

    if (value === undefined || value === null) {
      continue
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null) {
          continue
        }
        if (!isQueryParameterPrimitive(item)) {
          throw new Error(`Unsupported query parameter type for key: ${key}`)
        }
        entries.push(
          encodeQueryEntry({
            key,
            value: item,
          }),
        )
      }
      continue
    }

    if (!isQueryParameterPrimitive(value)) {
      throw new Error(`Unsupported query parameter type for key: ${key}`)
    }

    entries.push(
      encodeQueryEntry({
        key,
        value,
      }),
    )
  }

  return entries
}

/**
 * クエリパラメータからクエリ文字列を生成する。
 * @param params - クエリパラメータのオブジェクト
 * @returns クエリ文字列
 */
export const buildQueryString = (params?: Record<string, unknown>): string => {
  if (!params || Object.keys(params).length === 0) {
    return ''
  }

  return buildQueryEntries(params).join('&')
}
